import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import {isSerializablePlainData} from './SerializablePlainData';

/**
 * 装配体组件历史 adapter ID。
 */
export const ASSEMBLY_COMPONENT_HISTORY_ADAPTER_ID = 'history-domain:assembly-component';

/**
 * 组件 transform 快照。
 */
export interface ComponentTransformHistorySnapshot {
    /** 位置。 */
    readonly position: readonly [number, number, number];
    /** 四元数。 */
    readonly quaternion: readonly [number, number, number, number];
    /** 缩放。 */
    readonly scale: readonly [number, number, number];
}

/**
 * 装配体组件快照。
 *
 * 设计说明：
 * 1. Phase 1.1 只覆盖现有 move / rotate / fix / float 的主状态契约；
 * 2. Mate / MateSolver / AssemblySolverRecord 不在本阶段实现。
 */
export interface AssemblyComponentHistorySnapshot {
    /** 零部件实例 ID。 */
    readonly componentInstanceId: string;
    /** 装配体 ID。 */
    readonly assemblyId: string;
    /** 来源装配树节点 ID。 */
    readonly sourceNodeId?: string;
    /** 来源零件或子装配文件 UUID。 */
    readonly sourcePartFileUuid?: string;
    /** 父组件实例 ID。 */
    readonly parentComponentInstanceId?: string;
    //------ 装配体引用复制历史 开始------
    /** 引用复制来源装配节点 ID；普通移动/旋转记录为空。 */
    readonly referenceSourceNodeId?: string;
    /** 引用复制来源组件实例 ID；普通移动/旋转记录为空。 */
    readonly referenceSourceComponentInstanceId?: string;
    /** 引用复制体显示名称；普通移动/旋转记录为空。 */
    readonly displayName?: string;
    //------ 装配体引用复制历史 结束------
    /** 组件 transform。 */
    readonly transform: ComponentTransformHistorySnapshot;
    /** 固定状态。 */
    readonly fixationState: 'fixed' | 'floating';
    /** 是否被装配树抑制。 */
    readonly suppressed?: boolean;
}

/**
 * 装配体历史运行时存储。
 */
export interface AssemblyHistoryStore {
    /**
     * 写入组件状态。
     *
     * @param snapshot 组件状态快照。
     */
    readonly upsertComponentState: (snapshot: AssemblyComponentHistorySnapshot) => void;
    /**
     * 删除组件状态。
     *
     * @param componentInstanceId 组件实例 ID。
     */
    readonly deleteComponentState: (componentInstanceId: string) => void;
}

/**
 * 装配组件历史快照允许出现的字段。
 */
const ASSEMBLY_COMPONENT_HISTORY_SNAPSHOT_KEYS = new Set<string>([
    'componentInstanceId',
    'assemblyId',
    'sourceNodeId',
    'sourcePartFileUuid',
    'parentComponentInstanceId',
    //------ 装配体引用复制历史 开始------
    'referenceSourceNodeId',
    'referenceSourceComponentInstanceId',
    'displayName',
    //------ 装配体引用复制历史 结束------
    'transform',
    'fixationState',
    'suppressed',
]);

/**
 * 判断快照是否只包含 adapter 明确认识的字段。
 *
 * @param snapshot 装配组件快照。
 * @returns 是否只包含已知字段。
 */
function hasOnlyKnownAssemblySnapshotFields(snapshot: AssemblyComponentHistorySnapshot): boolean {
    return Object.keys(snapshot).every((key) => ASSEMBLY_COMPONENT_HISTORY_SNAPSHOT_KEYS.has(key));
}

/**
 * 判断数字元组长度是否符合预期。
 *
 * @param value 待判断值。
 * @param length 期望长度。
 * @returns 是否为指定长度数字数组。
 */
function isNumberTuple(value: readonly unknown[], length: number): boolean {
    return value.length === length && value.every((item) => typeof item === 'number');
}

/**
 * 规范化装配组件历史快照。
 *
 * 设计说明：
 * 旧事务中可能已经持久化了 `sourcePartFileUuid: undefined` 这类字段。IndexedDB 能保存
 * undefined，但 CAD 历史协议要求 snapshot 是纯可序列化主状态，所以这里只省略已知可选字段的
 * undefined，并保留对未知字段 / 运行时对象的拒绝能力。
 *
 * @param snapshot 装配组件快照。
 * @returns 已去除 undefined 可选字段并克隆 transform 数组的快照。
 */
function normalizeAssemblyComponentSnapshot(
    snapshot: AssemblyComponentHistorySnapshot,
): AssemblyComponentHistorySnapshot {
    return {
        componentInstanceId: snapshot.componentInstanceId,
        assemblyId: snapshot.assemblyId,
        ...(snapshot.sourceNodeId === undefined ? {} : {sourceNodeId: snapshot.sourceNodeId}),
        ...(snapshot.sourcePartFileUuid === undefined ? {} : {sourcePartFileUuid: snapshot.sourcePartFileUuid}),
        ...(snapshot.parentComponentInstanceId === undefined
            ? {}
            : {parentComponentInstanceId: snapshot.parentComponentInstanceId}),
        //------ 装配体引用复制历史 开始------
        ...(snapshot.referenceSourceNodeId === undefined
            ? {}
            : {referenceSourceNodeId: snapshot.referenceSourceNodeId}),
        ...(snapshot.referenceSourceComponentInstanceId === undefined
            ? {}
            : {referenceSourceComponentInstanceId: snapshot.referenceSourceComponentInstanceId}),
        ...(snapshot.displayName === undefined ? {} : {displayName: snapshot.displayName}),
        //------ 装配体引用复制历史 结束------
        transform: {
            position: [
                snapshot.transform.position[0],
                snapshot.transform.position[1],
                snapshot.transform.position[2],
            ],
            quaternion: [
                snapshot.transform.quaternion[0],
                snapshot.transform.quaternion[1],
                snapshot.transform.quaternion[2],
                snapshot.transform.quaternion[3],
            ],
            scale: [
                snapshot.transform.scale[0],
                snapshot.transform.scale[1],
                snapshot.transform.scale[2],
            ],
        },
        fixationState: snapshot.fixationState,
        ...(snapshot.suppressed === undefined ? {} : {suppressed: snapshot.suppressed}),
    };
}

/**
 * 判断装配组件快照是否为 plain data。
 *
 * @param snapshot 装配组件快照。
 * @returns 是否为 plain data。
 */
export function isPlainAssemblyComponentSnapshot(snapshot: AssemblyComponentHistorySnapshot): boolean {
    if (!hasOnlyKnownAssemblySnapshotFields(snapshot)) {
        return false;
    }

    const hasValidShape = typeof snapshot.componentInstanceId === 'string'
        && typeof snapshot.assemblyId === 'string'
        && (snapshot.sourceNodeId === undefined || typeof snapshot.sourceNodeId === 'string')
        && (snapshot.sourcePartFileUuid === undefined || typeof snapshot.sourcePartFileUuid === 'string')
        && (snapshot.parentComponentInstanceId === undefined || typeof snapshot.parentComponentInstanceId === 'string')
        //------ 装配体引用复制历史 开始------
        && (snapshot.referenceSourceNodeId === undefined || typeof snapshot.referenceSourceNodeId === 'string')
        && (
            snapshot.referenceSourceComponentInstanceId === undefined
            || typeof snapshot.referenceSourceComponentInstanceId === 'string'
        )
        && (snapshot.displayName === undefined || typeof snapshot.displayName === 'string')
        //------ 装配体引用复制历史 结束------
        && Array.isArray(snapshot.transform.position)
        && Array.isArray(snapshot.transform.quaternion)
        && Array.isArray(snapshot.transform.scale)
        && isNumberTuple(snapshot.transform.position, 3)
        && isNumberTuple(snapshot.transform.quaternion, 4)
        && isNumberTuple(snapshot.transform.scale, 3)
        && (snapshot.fixationState === 'fixed' || snapshot.fixationState === 'floating')
        && (snapshot.suppressed === undefined || typeof snapshot.suppressed === 'boolean');

    if (!hasValidShape) {
        return false;
    }

    return isSerializablePlainData(normalizeAssemblyComponentSnapshot(snapshot));
}

/**
 * 应用装配组件快照。
 *
 * @param input 补丁应用输入。
 */
function applyAssemblySnapshot(
    input: HistoryPatchApplyInput<AssemblyComponentHistorySnapshot, AssemblyHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.deleteComponentState(input.patch.entityId);

        return;
    }

    if (!snapshot.data) {
        throw new Error(`装配组件 ${input.patch.entityId} 缺少快照数据。`);
    }

    if (!isPlainAssemblyComponentSnapshot(snapshot.data)) {
        throw new Error(`装配组件 ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.upsertComponentState(normalizeAssemblyComponentSnapshot(snapshot.data));
}

/**
 * 比较装配组件快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areAssemblySnapshotsEqual(
    left: HistoryEntitySnapshot<AssemblyComponentHistorySnapshot>,
    right: HistoryEntitySnapshot<AssemblyComponentHistorySnapshot>,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建装配组件历史 adapter。
 *
 * @returns 装配组件历史 adapter。
 */
export function createAssemblyHistoryAdapter(): HistoryDomainAdapter<AssemblyComponentHistorySnapshot, AssemblyHistoryStore> {
    return {
        adapterId: ASSEMBLY_COMPONENT_HISTORY_ADAPTER_ID,
        kind: 'component-instance',
        applySnapshot: applyAssemblySnapshot,
        areSnapshotsEqual: areAssemblySnapshotsEqual as HistoryDomainAdapter<
            AssemblyComponentHistorySnapshot,
            AssemblyHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建装配组件历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 装配组件历史补丁。
 */
export function createAssemblyComponentHistoryPatch(
    patch: Omit<EntityPatch<AssemblyComponentHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<AssemblyComponentHistorySnapshot> {
    return {
        ...patch,
        entityKind: 'component-instance',
        adapterId: ASSEMBLY_COMPONENT_HISTORY_ADAPTER_ID,
    };
}
